import { Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { PurchaseAndPaymentUpdateRTM } from '@gains-com/realtime';
import { PendingPaymentMatcher } from './pending-payment-matcher.js';

type PurchaseIdMatch = Pick<PurchaseAndPaymentUpdateRTM, 'purchase_id'>;
type PaymentMethodIdMatch = Pick<PurchaseAndPaymentUpdateRTM, 'payment_method_id'>;
type PaymentIdMatch = Pick<PurchaseAndPaymentUpdateRTM, 'payment_id'>;
type PaymentMethodEntityIdMatch = Pick<
  PurchaseAndPaymentUpdateRTM,
  'payment_method_entity_id' | 'payment_method_entity_type_id' | 'currency_id'
>;
type PaymentEntityMatch = Pick<
  PurchaseAndPaymentUpdateRTM,
  'payment_entity_id' | 'payment_entity_type_id' | 'currency_id'
>;

export type PurchaseEntityQuery =
  | PurchaseIdMatch
  | PaymentMethodIdMatch
  | PaymentMethodEntityIdMatch
  | PaymentEntityMatch
  | PaymentIdMatch;

// Since we don't have the purchase id when the user initiates the payment, the
// queries discriminate on whichever identifying shape the purchase dialog had
// available at registration time.
function matchesAnyPurchaseEntityQuery(queries: PurchaseEntityQuery[], update: PurchaseAndPaymentUpdateRTM): boolean {
  return queries.some((query) => {
    if ('purchase_id' in query) {
      return query.purchase_id === update.purchase_id;
    }

    if ('payment_id' in query) {
      return query.payment_id === update.payment_id;
    }

    if ('payment_method_id' in query) {
      return query.payment_method_id === update.payment_method_id;
    }

    if ('payment_entity_id' in query && 'payment_entity_type_id' in query && 'currency_id' in query) {
      return (
        query.payment_entity_id === update.payment_entity_id &&
        query.payment_entity_type_id === update.payment_entity_type_id &&
        query.currency_id === update.currency_id
      );
    }

    if ('payment_method_entity_id' in query && 'payment_method_entity_type_id' in query && 'currency_id' in query) {
      return (
        query.payment_method_entity_id === update.payment_method_entity_id &&
        query.payment_method_entity_type_id === update.payment_method_entity_type_id &&
        query.currency_id === update.currency_id
      );
    }

    return false;
  });
}

@Injectable({
  providedIn: 'root',
})
export class ActivePurchaseViewService {
  private readonly _matcher = new PendingPaymentMatcher<PurchaseEntityQuery[]>({
    matches: matchesAnyPurchaseEntityQuery,
  });

  /** Backward-compat stream shim for existing observable consumers. */
  readonly activePurchase$ = toObservable(this._matcher.activeUpdate);

  /** Readonly signal surface for new consumers. */
  readonly activePurchase = this._matcher.activeUpdate;

  readonly activePurchasePaymentEntity = this._matcher.query;

  setActivePurchasePaymentEntity(query: PurchaseEntityQuery[] | PurchaseEntityQuery) {
    this._matcher.register(Array.isArray(query) ? query : [query]);
  }

  clearActivePurchasePayment() {
    this._matcher.clear();
  }

  updatePurchase(update: PurchaseAndPaymentUpdateRTM) {
    this._matcher.update(update);
  }
}
